import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { useToast } from '@shared/components/ui/use-toast';
import { portalVerifyCode, portalRequestCode, setPortalSession, getPortalToken, isPortalSessionExpired } from '@shared/services/portalService';

const PortalEntry = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const defaultPortalLogo = '/assets/ATC 1.png';

  // If already have a valid session, skip login and go straight to inductions
  useEffect(() => {
    const token = searchParams.get('token');
    if (getPortalToken() && !isPortalSessionExpired()) {
      // Store any new invite token from the URL, then skip to inductions
      if (token) localStorage.setItem('invite_token', token);
      navigate('/portal/inductions', { replace: true });
    }
  }, [navigate, searchParams]);

  // Load company logo from localStorage (set by admin in System Settings)
  // Portal automatically uses the same logo as the admin app
  useEffect(() => {
    const savedLogoUrl = localStorage.getItem('company_logo_url');
    if (savedLogoUrl && savedLogoUrl !== 'null') {
      setCompanyLogo(savedLogoUrl);
    }
  }, []);

  // Check if user came from invite email with token or email parameter
  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    
    if (token) {
      setInviteToken(token);
      localStorage.setItem('invite_token', token);
    }
    
    // If email is provided in URL (e.g., from project invitation), pre-fill it
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const handleVerifyCode = async () => {
    const codeString = code.join('');
    if (!email.trim() || codeString.length !== 6) {
      toast({
        title: 'Missing details',
        description: 'Enter your email and 6-digit code.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // If we have an invite token, verify against the invite
      // Otherwise verify against portal-request-code flow
      const result = await portalVerifyCode(email.trim(), codeString, inviteToken);
      if (!result?.ok || !result?.token) {
        throw new Error('Invalid code');
      }
      setPortalSession({
        token: result.token,
        expires_at: result.expires_at,
        email: email.trim(),
      });
      navigate('/portal/inductions');
    } catch (error) {
      toast({
        title: 'Verification failed',
        description: error?.message || 'Invalid or expired code.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async () => {
    if (!email.trim()) {
      toast({
        title: 'Email required',
        description: 'Enter your email address to receive your access code.',
        variant: 'destructive',
      });
      return;
    }

    setRequestingCode(true);
    setCodeSent(false);
    try {
      const result = await portalRequestCode(email.trim());
      if (result?.ok) {
        setCodeSent(true);
        toast({
          title: 'Code sent',
          description: 'Check your email for your access code.',
        });
      } else {
        const errCode = result?.error?.code;
        if (errCode === 'NO_INVITE') {
          toast({
            title: 'No invitation found',
            description: 'No invitation found for this email. Contact your depot admin.',
            variant: 'destructive',
          });
        } else if (errCode === 'RATE_LIMITED') {
          toast({
            title: 'Please wait',
            description: 'A code was recently sent. Check your email or wait a moment.',
            variant: 'destructive',
          });
        } else {
          throw new Error(result?.error?.message || 'Failed to request code');
        }
      }
    } catch (error) {
      // Handle non-2xx responses (Supabase client wraps them as errors)
      const body = error?.context?.body;
      const errCode = body?.error?.code;
      if (errCode === 'NO_INVITE') {
        toast({
          title: 'No invitation found',
          description: 'No invitation found for this email. Contact your depot admin.',
          variant: 'destructive',
        });
      } else if (errCode === 'RATE_LIMITED') {
        toast({
          title: 'Please wait',
          description: 'A code was recently sent. Check your email or wait a moment.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Request failed',
          description: error?.message || 'Could not send code. Try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setRequestingCode(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.padEnd(6, '').split('').slice(0, 6);
      setCode(newCode);
      // Focus last filled input
      const lastIndex = pastedData.length - 1;
      const lastInput = document.getElementById(`code-${Math.min(lastIndex, 5)}`);
      if (lastInput) lastInput.focus();
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Login Form */}
      <div className="w-full lg:w-[30%] flex flex-col bg-[#1a1a1a] relative overflow-hidden">
        {/* Carbon fiber diagonal pattern background */}
        <div 
          className="absolute inset-0 opacity-30" 
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(0, 0, 0, 0.3) 10px,
              rgba(0, 0, 0, 0.3) 20px
            ),
            repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 10px,
              rgba(0, 0, 0, 0.3) 10px,
              rgba(0, 0, 0, 0.3) 20px
            )`
          }}
        />
        
        {/* Content Layer */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Customer Logo at Top - Fixed Size Container */}
          <div className="pt-16 pb-6 px-8">
            <div className="flex justify-center items-center h-72">
              <img
                src={companyLogo || defaultPortalLogo}
                alt="Company Logo"
                className="max-h-72 max-w-full object-contain"
              />
            </div>
          </div>
          
          {/* Form positioned in lower portion */}
          <div className="flex-1 px-8 pb-12 pt-4">
            <div className="w-full max-w-md mx-auto space-y-8">
            {/* Welcome Message */}
            <div className="text-center space-y-4">
              <h1 className="text-6xl font-bold text-white">Welcome</h1>
              <p className="text-base text-gray-400">
                Enter your email and 6-digit code from your invitation
              </p>
            </div>

            {/* Code Input (Main Feature) */}
            <div className="space-y-4">
              <h2 className="text-center text-2xl font-semibold text-white">Enter Code</h2>
              <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-semibold bg-white text-gray-900 border-2 border-gray-600 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-400 outline-none transition-all"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            {/* Email Input (below code or primary when requesting) */}
            <div className="space-y-4">
              <div>
                <label className="block text-base font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  type="email"
                  autoComplete="email"
                  className="h-16 text-xl bg-white text-gray-900 border-gray-600 focus:border-orange-500 focus:ring-orange-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleVerifyCode();
                    }
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                className="w-full h-16 text-xl font-bold bg-[#f59e0b] hover:bg-[#d97706] text-gray-900" 
                onClick={handleVerifyCode} 
                disabled={loading || requestingCode}
              >
                {loading ? 'Verifying…' : 'Sign In'}
              </Button>

              <Button
                variant="outline"
                className="w-full h-12 text-base font-medium border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
                onClick={handleRequestCode}
                disabled={loading || requestingCode}
              >
                {requestingCode ? 'Sending…' : codeSent ? '✓ Code Sent — Check Email' : 'Request My Code'}
              </Button>
            </div>

            {/* Footer text */}
            <div className="text-center pt-4">
              <p className="text-base text-gray-500">
                No sign up required - contact your depot admin
              </p>
            </div>

            {/* APEXIAR Logo branding */}
            <div className="pt-8 flex justify-center">
              <img 
                src="/assets/powered.png" 
                alt="Powered by APEXIAR" 
                className="h-32 w-auto object-contain opacity-90"
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Right Panel - Hero Image (Preset) */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-blue-900 to-blue-700">
        <div className="absolute inset-0">
          <img
            src="/assets/Generic transport 2.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Optional overlay content */}
        <div className="relative h-full flex items-end p-12">
          <div className="text-white space-y-4 max-w-[800px]">
            <h1 className="text-7xl font-bold drop-shadow-lg leading-tight text-center">Supplier Portal</h1>
            <p className="text-3xl text-blue-100 drop-shadow-md">
              Manage your site access, inductions, credentials and project documentation in one secure place.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalEntry;
